import React, { useEffect, useMemo, useRef, useState } from "react";
import { CommunityMonthSelector } from "../../../components/community/month-selector";
import { READING_PLAN_MONTHS } from "../../data/reading-plans";
import { CommunitySelector } from "../../../components/community/community-selector";
import { BookCard } from "./components/book-card";
import { useReadingPlan } from "../../hooks/use-reading-plan";
import { LibrosAPIs } from "../../../services/api/Libros";
import { CancelRequestController } from "../../../util/cancel-request-controller";
import { READING_LEVELS } from "../../data/reading-levels";
import moment from "moment";
import { useTranslationsById } from "../../../hooks/use-translations-by-id";
import { useLanguage } from "../../../hooks/use-language";

/**
 * Sección de planes de lectura mensuales.
 *
 * Coordina la selección de mes, el plan activo y la carga de libros asociados.
 */
export const ReadingPlans = React.forwardRef((props, ref) => {
	const lang = useLanguage();
	const texts = useTranslationsById({ mainSectionName: "reading_resources", language: lang });
	const months = useMemo(() => {
		return [...READING_PLAN_MONTHS].map((month) => {
			return {
				...month,
				name: texts.getText({
					id: month?.name?.translationPath,
					defaultText: month?.name?.default,
				}),
			};
		});
	}, [texts.fullData, lang]);
	const visibleMonths = useMemo(() => {
		return Array.isArray(months)
			? [...months].filter(
					(month) =>
						month?.visible === true &&
						(month?.empty === false || month?.empty === undefined),
				)
			: [];
	}, [months]);

	const defaultMonth = useMemo(() => {
		return (
			visibleMonths.find(
				(month) =>
					moment().isSame(month?.date, "month") && moment().isSame(month?.date, "year"),
			)?.id || visibleMonths[0]?.id
		);
	}, [visibleMonths]);

	const defaultReadingLevelId = "primeros lectores";
	const [currentMonth, setCurrentMonth] = useState(defaultMonth);
	const [currentReadingLevelId, setCurrentReadingLevelId] = useState(defaultReadingLevelId);
	const readingPlan = useReadingPlan({ month: currentMonth });
	const [loadingBook, setLoadingBook] = useState(true);
	const [book, setBook] = useState({});
	const prevControllerRef = useRef(null);
	const readingLevels = [...READING_LEVELS].map((readingLevel) => {
		return {
			...readingLevel,
			name: texts.getText({
				id: readingLevel?.name?.translationPath,
				defaultText: readingLevel?.name?.default,
			}),
		};
	});

	const handleReadingLevelSelection = async ({ readingLevelId }) => {
		if (prevControllerRef.current) {
			prevControllerRef.current.abort();
		}
		const controller = new CancelRequestController();
		prevControllerRef.current = controller;
		setLoadingBook(true);
		setCurrentReadingLevelId(readingLevelId);
		let bookId = null;
		if (readingLevelId?.toLowerCase() === "primeros lectores") {
			bookId = readingPlan?.plan?.books[0];
		} else if (readingLevelId?.toLowerCase() === "lectores intermedios") {
			bookId = readingPlan?.plan?.books[1];
		} else if (readingLevelId?.toLowerCase() === "lectores avanzados") {
			bookId = readingPlan?.plan?.books[2];
		}
		if (bookId) {
			await LibrosAPIs.obtenerDatosFichaLibro({
				abortSignal: controller.signal,
				idLibro: bookId,
			})
				.then((_book) => {
					setBook({ ..._book, idLibro: bookId });
					setLoadingBook(false);
				})
				.catch((error) => console.log(error));
		}
	};

	useEffect(() => {
		handleReadingLevelSelection({
			readingLevelId: defaultReadingLevelId,
		});
	}, [readingPlan?.plan?.month]);

	return (
		<div className="section reading-plans w-100">
			<div className="container">
				<div ref={ref} style={{ scrollMarginTop: 80 }}>
					<h2
						className="heading-3 onbackground-color title-font text-left"
						style={{ fontWeight: 400 }}
					>
						{texts.getText({
							id: "planes.titulo",
							defaultText: "Monthly reading plan",
						})}
					</h2>
				</div>
				<p
					className="body-base md:body-l onbackground-color my-5 col-12 col-lg-7 px-0"
					style={{ fontWeight: 600 }}
				>
					{texts.getText({
						id: "planes.descripcion",
						defaultText:
							"Our 12-month reading plan to easily map out your year includes: 3 curated books recommended every single month and ready-to-use reading guides for deep comprehension",
					})}
				</p>
				<CommunityMonthSelector
					months={months}
					defaultMonthId={defaultMonth}
					onSelect={(month) => {
						setCurrentMonth(month?.id);
					}}
				/>
				<div className="row m-0 p-0 d-flex justify-content-center mt-4">
					<div className="col-12 col-lg-4 p-0 m-0 d-flex">
						<div className="reading-plans-sidebar w-100">
							<div className="reading-plans-sidebar-copy">
								<p className="reading-plans-sidebar-eyebrow mb-2">
									{texts.getText({
										id: "planes.tematica",
										defaultText: "Theme",
									})}
								</p>
								<h2 className="reading-plans-sidebar-theme mb-4 subheading-1">
									{readingPlan?.plan?.theme?.[lang] || readingPlan?.plan?.theme?.es}
								</h2>
								<p className="reading-plans-sidebar-question body-base">
									{readingPlan?.plan?.question?.[lang] ||
										readingPlan?.plan?.question?.es}
								</p>
							</div>
							<div className="reading-plans-sidebar-selector">
								<CommunitySelector
									color={"black"}
									defaultItemId={currentReadingLevelId}
									data={readingLevels}
									onSelect={(readingLevel) => {
										handleReadingLevelSelection({
											readingLevelId: readingLevel?.id,
										});
									}}
								/>
							</div>
						</div>
					</div>
					<div className="col-12 col-lg-8 p-0 m-0">
						<BookCard book={book} loading={loadingBook} lang={lang} />
					</div>
				</div>
			</div>
		</div>
	);
});
